import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Trash2, Plus } from 'lucide-react';
import type { Article } from '@dj/types';
import { apiFetch } from '@/lib/api';

export default function ArticlesListPage() {
  const [articles, setArticles] = useState<Omit<Article, 'content'>[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const res = await apiFetch<Omit<Article, 'content'>[]>('/api/v1/admin/articles');
    if (res.success) setArticles(res.data);
    else setError(res.error?.message ?? 'Failed to load articles.');
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(id: string, title: string) {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeleting(id);
    const res = await apiFetch(`/api/v1/admin/articles/${id}`, { method: 'DELETE' });
    if (res.success) setArticles((prev) => prev.filter((a) => a.id !== id));
    else alert(res.error?.message ?? 'Delete failed.');
    setDeleting(null);
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Articles</h1>
          <p className="mt-0.5 text-sm text-zinc-400">{articles.length} total</p>
        </div>
        <Link
          to="/articles/new"
          className="flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
        >
          <Plus size={15} />
          New Article
        </Link>
      </div>

      {loading && <p className="text-sm text-zinc-500">Loading…</p>}
      {error   && <p className="text-sm text-red-400">{error}</p>}

      {!loading && !error && articles.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-700 py-16 text-center">
          <p className="text-sm text-zinc-500">No articles yet.</p>
          <Link to="/articles/new" className="mt-2 inline-block text-sm text-indigo-400 hover:underline">
            Write your first article →
          </Link>
        </div>
      )}

      {!loading && articles.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-zinc-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Published</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.id} className="border-b border-zinc-800/50 hover:bg-zinc-900/40">
                  <td className="px-4 py-3">
                    <p className="font-medium text-white">{article.title}</p>
                    <p className="font-mono text-xs text-zinc-500">{article.slug}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{article.category}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      article.status === 'published'
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-zinc-700/50 text-zinc-400'
                    }`}>
                      {article.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                    {article.published_at
                      ? new Date(article.published_at).toLocaleDateString('en')
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/articles/${article.id}/edit`}
                        className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </Link>
                      <button
                        onClick={() => handleDelete(article.id, article.title)}
                        disabled={deleting === article.id}
                        className="rounded p-1.5 text-zinc-400 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
